import React from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';

import AvatarSkeleton from '@atlaskit/avatar/Skeleton';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { AvatarRelay, type AvatarRelayProps } from '@atlassian/avatar-relay/avatar-relay';
import { useResolveAgentAri } from '@atlassian/conversation-assistant-agent/controllers/agent-ari';
import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

import type { agentIdentityAccountDisplayAgentQuery } from './__generated__/agentIdentityAccountDisplayAgentQuery.graphql';
import type { agentIdentityAccountDisplayQuery } from './__generated__/agentIdentityAccountDisplayQuery.graphql';

type AgentIdentityAccountData = {
	readonly name?: string | null;
	readonly ' $fragmentSpreads': import('relay-runtime').FragmentRefs<'avatarRelay_user'>;
};

type AgentIdentityAccountDisplayProps = {
	agentAri?: string | null;
	agentId?: string;
	cloudId?: string;
	identityAccountId?: string | null;
	onNameResolved?: (name: string) => void;
	size: 'small' | 'large';
	testId?: string;
	textXcss?: React.ComponentProps<typeof Box>['xcss'];
};

type AgentIdentityAccountDisplayPresentationProps = Pick<
	AgentIdentityAccountDisplayProps,
	'onNameResolved' | 'size' | 'testId' | 'textXcss'
>;

const AgentIdentityAccountDisplayFallback = ({
	size,
	testId,
	textXcss,
}: AgentIdentityAccountDisplayPresentationProps): React.JSX.Element | null => {
	const isLarge = size === 'large';

	return (
		<>
			<AvatarSkeleton appearance="hexagon" size={isLarge ? 'large' : 'small'} />
			<Box testId={testId} xcss={textXcss}>
				<Skeleton
					width={isLarge ? '160px' : '72px'}
					height={isLarge ? '24px' : '12px'}
					borderRadius={token('radius.xsmall')}
					isShimmering
				/>
			</Box>
		</>
	);
};

const AgentIdentityAccountDisplayContent = ({
	identityAccount,
	onNameResolved,
	size,
	testId,
	textXcss,
}: AgentIdentityAccountDisplayPresentationProps & {
	identityAccount: AgentIdentityAccountData;
}): React.JSX.Element | null => {
	const relayName = identityAccount.name?.trim();

	React.useEffect(() => {
		if (relayName) {
			onNameResolved?.(relayName);
		}
	}, [onNameResolved, relayName]);

	if (!relayName) {
		return null;
	}

	const avatarSize: AvatarRelayProps['size'] = size === 'large' ? 'large' : 'small';

	if (size === 'large') {
		return (
			<>
				<AvatarRelay userFragment={identityAccount} size={avatarSize} name={relayName} />
				<Heading size="large" testId={testId}>
					{relayName}
				</Heading>
			</>
		);
	}

	return (
		<>
			<AvatarRelay userFragment={identityAccount} size={avatarSize} name={relayName} />
			<Box testId={testId} xcss={textXcss}>
				{relayName}
			</Box>
		</>
	);
};

const AgentIdentityAccountDisplayFromRelay = ({
	accountId,
	onNameResolved,
	size,
	testId,
	textXcss,
}: AgentIdentityAccountDisplayPresentationProps & {
	accountId: string;
}): React.JSX.Element | null => {
	di(useLazyLoadQuery);

	const data = useLazyLoadQuery<agentIdentityAccountDisplayQuery>(
		graphql`
			query agentIdentityAccountDisplayQuery($identityAccountId: ID!) {
				user(accountId: $identityAccountId) {
					name
					...avatarRelay_user
				}
			}
		`,
		{ identityAccountId: accountId },
		{ fetchPolicy: 'store-or-network' },
	);

	if (!data.user) {
		return null;
	}

	return (
		<AgentIdentityAccountDisplayContent
			identityAccount={data.user}
			onNameResolved={onNameResolved}
			size={size}
			testId={testId}
			textXcss={textXcss}
		/>
	);
};

const AgentIdentityAccountDisplayFromAgentRelay = ({
	agentAri: existingAgentAri,
	agentId,
	cloudId,
	...presentationProps
}: AgentIdentityAccountDisplayPresentationProps & {
	agentAri?: string | null;
	agentId: string;
	cloudId: string;
}): React.JSX.Element | null => {
	const { agentAri } = useResolveAgentAri({
		agentId,
		existingAgentAri,
		cloudId,
	});
	const data = useLazyLoadQuery<agentIdentityAccountDisplayAgentQuery>(
		graphql`
			query agentIdentityAccountDisplayAgentQuery($agentAri: ID!, $skip: Boolean!) {
				agentStudio_agentById(id: $agentAri) @optIn(to: "AgentStudio") @skip(if: $skip) {
					__typename
					... on AgentStudioAssistant {
						identityAccountId
					}
				}
			}
		`,
		{
			agentAri: agentAri ?? '',
			skip: !agentAri,
		},
		{ fetchPolicy: 'store-or-network' },
	);
	const identityAccountId =
		data.agentStudio_agentById?.__typename === 'AgentStudioAssistant'
			? data.agentStudio_agentById.identityAccountId
			: undefined;
	const accountId = normalizeAccountIdFromIdentityAri(identityAccountId);

	if (!accountId) {
		return null;
	}

	return <AgentIdentityAccountDisplayFromRelay accountId={accountId} {...presentationProps} />;
};

export const AgentIdentityAccountDisplay = ({
	agentAri,
	agentId,
	cloudId,
	identityAccountId,
	onNameResolved,
	size,
	testId,
	textXcss,
}: AgentIdentityAccountDisplayProps): React.JSX.Element | null => {
	const accountId = normalizeAccountIdFromIdentityAri(identityAccountId);
	const canHydrateIdentityFromAgent = Boolean(agentId && cloudId);

	if (!accountId && !canHydrateIdentityFromAgent) {
		return null;
	}

	return (
		<ErrorBoundary fallback={null} resetKeys={[accountId, agentAri, agentId, cloudId]}>
			<React.Suspense
				fallback={
					<AgentIdentityAccountDisplayFallback size={size} testId={testId} textXcss={textXcss} />
				}
			>
				{accountId ? (
					<AgentIdentityAccountDisplayFromRelay
						accountId={accountId}
						onNameResolved={onNameResolved}
						size={size}
						testId={testId}
						textXcss={textXcss}
					/>
				) : agentId && cloudId ? (
					<AgentIdentityAccountDisplayFromAgentRelay
						agentAri={agentAri}
						agentId={agentId}
						cloudId={cloudId}
						onNameResolved={onNameResolved}
						size={size}
						testId={testId}
						textXcss={textXcss}
					/>
				) : null}
			</React.Suspense>
		</ErrorBoundary>
	);
};
